// MFA-challenge page — verschijnt na succesvolle wachtwoord-verificatie wanneer
// user MFA-enrolled is. State komt via react-router navigate(). PRD-01 §9.2.

import { useState, useEffect, useRef, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { api, ApiError } from "@/lib/api";
import { authStore, type CurrentUser } from "@/lib/auth";

interface VerifyResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: CurrentUser;
  backupCodes?: string[];
}

export function MfaPromptPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as
    | { mfaChallenge?: string; mustEnroll?: boolean; tenantSlug?: string }
    | undefined;

  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [qrDataUri, setQrDataUri] = useState<string | null>(null);
  const [otpauthUri, setOtpauthUri] = useState<string | null>(null);
  const [showBackupCodes, setShowBackupCodes] = useState<{
    codes: string[];
    target: string;
  } | null>(null);

  useEffect(() => {
    if (!state?.mfaChallenge) {
      navigate("/login", { replace: true });
      return;
    }
    inputRef.current?.focus();
  }, [state, navigate]);

  useEffect(() => {
    if (state?.mustEnroll && state.mfaChallenge && !qrDataUri) {
      api
        .post<{ qrDataUri: string; otpauthUri: string }>(
          "/v1/auth/mfa/enroll-with-challenge",
          { mfaChallenge: state.mfaChallenge },
          { skipAuth: true }
        )
        .then((r) => {
          setQrDataUri(r.qrDataUri);
          setOtpauthUri(r.otpauthUri);
        })
        .catch(() => setErrorKey("errors.internal"));
    }
  }, [state, qrDataUri]);

  if (!state?.mfaChallenge) return null;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!state?.mfaChallenge) return;
    setErrorKey(null);
    setLoading(true);
    try {
      const data = await api.post<VerifyResponse>(
        "/v1/auth/mfa/verify",
        { mfaChallenge: state.mfaChallenge, code: code.replace(/\s/g, "") },
        { skipAuth: true, tenantSlug: state.tenantSlug }
      );
      authStore.setSession(
        data.accessToken,
        data.refreshToken,
        data.user,
        state.tenantSlug ?? ""
      );
      // Role-aware redirect: admin → /admin, counselor → /counselor,
      // municipality → /municipality, anders /dashboard.
      const role = data.user.role;
      const target =
        role === "admin" || role === "super_admin"
          ? "/admin/users"
          : role === "counselor"
            ? "/counselor"
            : role === "municipality"
              ? "/municipality"
              : "/dashboard";
      // Bij first-time enrollment: toon backup-codes screen vóór redirect
      if (data.backupCodes && data.backupCodes.length > 0) {
        setShowBackupCodes({ codes: data.backupCodes, target });
        return;
      }
      // Hard-reload navigatie — zelfde reden als LoginPage: voorkomt race
      // tussen authStore-update en ProtectedRoute's re-render.
      window.location.replace(target);
    } catch (err) {
      setErrorKey(err instanceof ApiError ? err.i18nKey : "errors.internal");
    } finally {
      setLoading(false);
    }
  }

  if (showBackupCodes) {
    return (
      <main className="min-h-screen flex items-center justify-center px-4 py-12 bg-gradient-to-br from-brand-50 to-white">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-amber-200 p-8">
          <h1 className="text-2xl font-semibold mb-2 text-amber-900">
            {t("auth.mfa.backupTitle", "Bewaar je backup-codes")}
          </h1>
          <p className="text-sm text-neutral-700 mb-4">
            {t(
              "auth.mfa.backupBody",
              "Dit zijn eenmalige backup-codes voor als je je authenticator-app kwijt bent. Bewaar ze op een veilige plaats — ze worden niet opnieuw getoond."
            )}
          </p>
          <ul className="grid grid-cols-2 gap-2 mb-6 font-mono text-sm bg-neutral-50 p-3 rounded-lg">
            {showBackupCodes.codes.map((c) => (
              <li key={c} className="text-center py-1">
                {c}
              </li>
            ))}
          </ul>
          <button
            onClick={() => navigator.clipboard?.writeText(showBackupCodes.codes.join("\n"))}
            className="w-full mb-3 px-4 py-2 bg-neutral-100 hover:bg-neutral-200 text-sm text-neutral-700 rounded-lg"
          >
            {t("auth.mfa.backupCopy", "Kopieer naar klembord")}
          </button>
          <button
            onClick={() => window.location.replace(showBackupCodes.target)}
            className="w-full px-4 py-2.5 bg-brand-500 hover:bg-brand-600 text-white font-medium rounded-lg"
          >
            {t("auth.mfa.backupContinue", "Bewaard, ga verder")}
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen flex items-center justify-center px-4 py-12 bg-gradient-to-br from-brand-50 to-white">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-neutral-200 p-8">
        <h1 className="text-2xl font-semibold mb-1">
          {state.mustEnroll
            ? t("auth.mfa.enrollTitle", "MFA instellen")
            : t("auth.mfa.title", "Tweestapsverificatie")}
        </h1>
        <p className="text-sm text-neutral-600 mb-6">
          {state.mustEnroll
            ? t(
                "auth.mfa.enrollSubtitle",
                "Scan de QR-code met je authenticator-app (Google Authenticator / Authy / 1Password) en vul de eerste 6-cijferige code in."
              )
            : t(
                "auth.mfa.subtitle",
                "Vul de 6-cijferige code uit je authenticator-app in, of gebruik een backup-code."
              )}
        </p>

        {state.mustEnroll && qrDataUri && (
          <div className="mb-6 text-center">
            <img
              src={qrDataUri}
              alt="MFA QR-code"
              className="mx-auto w-48 h-48 border border-neutral-200 rounded-lg"
            />
            <details className="mt-2 text-xs text-neutral-500">
              <summary className="cursor-pointer">
                {t("auth.mfa.manualKey", "Handmatig invoeren")}
              </summary>
              <code className="break-all">{otpauthUri}</code>
            </details>
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4" aria-busy={loading}>
          <div>
            <label htmlFor="mfa-code" className="block text-sm font-medium text-neutral-700 mb-1">
              {t("auth.mfa.code", "Code")}
            </label>
            <input
              id="mfa-code"
              ref={inputRef}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              className="w-full px-3 py-2 text-lg font-mono tracking-widest text-center border border-neutral-300 rounded-lg focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
              placeholder="123 456"
            />
            <p className="mt-1 text-xs text-neutral-500">
              {t("auth.mfa.backupHint", "Backup-code? Format: XXXX-XXXX")}
            </p>
          </div>

          {errorKey && (
            <div
              role="alert"
              className="px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800"
            >
              {t(errorKey, t("errors.internal", "Er ging iets mis"))}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full px-4 py-2.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-60 text-white font-medium rounded-lg"
          >
            {loading ? t("auth.mfa.verifying", "Verifiëren…") : t("auth.mfa.verify", "Verifiëren")}
          </button>
        </form>

        <button
          onClick={() => navigate("/login", { replace: true })}
          className="mt-4 w-full text-sm text-neutral-600 hover:text-neutral-900"
        >
          {t("common.back", "Terug naar inloggen")}
        </button>
      </div>
    </main>
  );
}
